import React from 'react';
import { Typography } from 'antd';
import {
  BarChartOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import styles from '../../styles/welcomeScreen.module.css';

const { Title, Text } = Typography;

/**
 * WelcomeScreen Component
 * Displays onboarding and example queries when chat is empty
 */
const WelcomeScreen = ({ onSelectExample, userName, compact = false }) => {
  const exampleQueries = [
    {
      category: 'Analytics',
      icon: <BarChartOutlined />,
      color: '#1677ff',
      examples: [
        'How many incidents do I have this month?',
        'Show me average HRST scores by building',
        'What are the top 5 facilities by incident count?',
      ],
    },
    {
      category: 'Data Exploration',
      icon: <DatabaseOutlined />,
      color: '#52c41a',
      examples: [
        'Show me incidents from last week',
        'List patients with high risk scores',
        'Find all unresolved safety issues',
      ],
    },
    {
      category: 'Insights',
      icon: <FileTextOutlined />,
      color: '#faad14',
      examples: [
        "What's my overall health score?",
        'Summarize my module performance',
        'Give me an executive overview',
      ],
    },
    {
      category: 'Quick Actions',
      icon: <ThunderboltOutlined />,
      color: '#722ed1',
      examples: [
        'Compare this month vs last month',
        'Show me trending metrics',
        'What needs my attention?',
      ],
    },
  ];

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const exampleQueriesToRender = compact
    ? exampleQueries.map((category) => ({
        ...category,
        examples: category.examples.slice(0, 2),
      }))
    : exampleQueries;

  return (
    <div className={`${styles.welcomeScreen} ${compact ? styles.welcomeScreenCompact : ''}`}>
      {/* Header */}
      <div className={styles.welcomeHeader}>
        <div className={styles.welcomeIcon}>
          <div className={styles.aiGradientIcon}>
            <QuestionCircleOutlined />
          </div>
        </div>
        <Title level={2} className={styles.welcomeTitle}>
          {greeting()}{userName ? `, ${userName}` : ''}!
        </Title>
        <Text className={styles.welcomeSubtitle}>
          Ask me anything about your data, analytics, or reports. I'll help you find insights and answer questions.
        </Text>
      </div>

      {/* Example Queries */}
      <div className={styles.exampleCategories}>
        {exampleQueriesToRender.map((category, categoryIndex) => (
          <div key={categoryIndex} className={styles.exampleCategory}>
            <div className={styles.categoryHeader}>
              <div
                className={styles.categoryIcon}
                style={{ color: category.color }}
              >
                {category.icon}
              </div>
              <Text className={styles.categoryTitle}>{category.category}</Text>
            </div>

            <div className={styles.exampleChips}>
              {category.examples.map((example, exampleIndex) => (
                <div
                  key={exampleIndex}
                  className={styles.exampleChip}
                  onClick={() => onSelectExample(example)}
                  style={{ borderColor: category.color }}
                >
                  <Text className={styles.chipText}>{example}</Text>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Capabilities */}
      <div className={styles.capabilities}>
        <Text className={styles.capabilitiesTitle}>I can help you:</Text>
        <ul className={styles.capabilitiesList}>
          <li>📊 Analyze trends and patterns in your data</li>
          <li>🔍 Search and filter specific records</li>
          <li>📈 Calculate aggregations and statistics</li>
          <li>💡 Generate insights from your reports</li>
          <li>🎯 Identify areas needing attention</li>
        </ul>
        <div className={styles.poweredBy}>
          <Text className={styles.poweredByText}>
            Powered by Advanced AI · BICollab Intelligence Platform
          </Text>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
